import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import BaseSubscriptionSelect, {
  type SubscriptionOption,
} from 'lumen/components/settings/users/base-subscription-select';

interface SubscriptionMultiSelectSignature {
  Args: {
    emptyLabel: string;
    onChange: (
      selectedOptions: SubscriptionOption[],
      isExcludeMode: boolean,
    ) => void;
    userId?: string;
    value: SubscriptionOption[];
  };
}

/**
 * Checkbox multi-select with async loading and a select all option. Select
 * all switches to exclude mode: the selected options are then the
 * subscriptions to leave out.
 */
export default class SubscriptionMultiSelect extends BaseSubscriptionSelect<SubscriptionMultiSelectSignature> {
  // lint:intl only counts `intl.t` calls in classes that declare the service
  // themselves, so it can't be left to the base class.
  @service declare intl: Services['intl'];

  protected override get fetchParams(): Record<string, string | number> {
    const { userId } = this.args;

    return userId ? { ...super.fetchParams, user: userId } : super.fetchParams;
  }

  // The select all option is only offered on the unfiltered list.
  get selectAllLabel(): string | undefined {
    if (this.searchQuery !== '' || this.totalResults === 0) {
      return undefined;
    }

    return this.intl.t('settings.users.add_subscription.select_all', {
      count: this.totalResults,
    });
  }

  @action
  buildLabel(
    selectedCount: number,
    {
      unsafe_isExcludeMode: isExcludeMode = false,
    }: { unsafe_isExcludeMode?: boolean },
  ): string {
    // In exclude mode the selection holds the unticked subscriptions.
    if (isExcludeMode && selectedCount === 0) {
      return this.intl.t('settings.users.filter.all_subscriptions');
    }

    const count = isExcludeMode
      ? this.totalResults - selectedCount
      : selectedCount;

    if (count === 0) {
      return this.args.emptyLabel;
    }

    return this.intl.t(
      'settings.users.add_subscription.trigger_label_with_count',
      { count },
    );
  }

  @action
  handleChange(
    selectedOptions: SubscriptionOption[],
    {
      unsafe_isExcludeMode: isExcludeMode = false,
    }: { unsafe_isExcludeMode?: boolean },
  ): void {
    this.args.onChange(selectedOptions, isExcludeMode);
  }

  <template>
    <PrismComboSelectInput
      @buildLabelWithCount={{this.buildLabel}}
      @isSearchable={{true}}
      @onChange={{this.handleChange}}
      @onOpen={{this.onOpen}}
      @onSearch={{this.onSearch}}
      @options={{this.options}}
      @selectAllLabel={{this.selectAllLabel}}
      @unsafe_onLoadMore={{this.loadMoreTask.perform}}
      @value={{@value}}
    />
  </template>
}

export type { SubscriptionOption };
